import {
    Breakpoints,
    Container,
    ContainerPadding,
    CTA,
    Layout,
    LayoutRowGap,
    Text,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import {Checkmark, Close} from '@volkswagen-onehub/icons-core'
import {globalColors} from '@volkswagen-onehub/tokens'

import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {
    COUPON_REQUEST_FORM_ID,
    CouponRequestForm,
} from '../form/CouponRequestForm'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import {setLoading} from '../../features/general/generalSlice'
import axios from 'axios'
import {openAlertSystemLayer} from './launchers/AlertSystemLayerHandle'
import {AppDispatch} from '../thunk'
import {
    CloseLayerCTA,
    FORM_BORDER_COLOR,
    LayerHeaderCloseCTA,
    LayerHeaderContainer,
    LayerHeaderTitle,
} from '../../shared/classes'
import {closeCouponLayer} from './launchers/CouponRequestLayerHandle'
import {setResentEmail} from '../../features/userData/userDataSlice'
import {
    eventInfoCommon,
    TrackingAction,
    trackingData,
    trackingEvent,
} from '../../shared/trackingServiceHelper'
import store from '../store'
import {getSourceKey, getVersionKey} from '../../shared/utilities'
import {Endpoints} from '../../shared/variables'

const COUPON_LAYER_ID = 'coupon-request-layer'
const COUPON_LAYER_TITLE_ID = 'coupon-request-layer-title'
const WHITE_BACKGROUND = globalColors.white['000']

const ResentEmailTextContainer = styled(Text)`
    margin: ${(props) => props.theme.size.static300} 0 0;
`
const ResentEmailText = styled(Text)`
    display: flex;
    align-items: center;

    svg {
        margin-right: ${(props) => props.theme.size.static200};
        width: ${(props) => props.theme.size.static400};
    }
`

const FooterTextContainer = styled.div`
    border-top: ${(props) => props.theme.size.border.heavy} solid
        ${(props) => props.theme.colors.border.disabled};
    padding: ${(props) => props.theme.size.dynamic0250} 0 0;
    margin: ${(props) => props.theme.size.dynamic0250} 0 0;
`

const ContainerFooterCTA = styled.div`
    margin: ${(props) => props.theme.size.static400} 0 0;
`

const CouponLayerHeaderContainer = styled(LayerHeaderContainer)`
    background-color: ${WHITE_BACKGROUND};

    #${COUPON_LAYER_ID} > div > div > div:has(> &) {
        position: sticky;
        top: 0;
        background-color: ${WHITE_BACKGROUND};
        width: 100%;
        z-index: 2;
    }
`

const SubmitContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    box-sizing: border-box;
    width: 100%;
    padding-top: ${(props) => props.theme.size.static250};
    padding-right: ${(props) => props.theme.size.static250};
    padding-bottom: ${(props) => props.theme.size.static250};
    padding-left: ${(props) => props.theme.size.grid001};
    border-top: ${(props) => props.theme.size.border.light} solid
        ${FORM_BORDER_COLOR};
    background-color: ${(props) => props.theme.colors.background.primary};

    @media (min-width: ${Breakpoints.b560}px) {
        padding-left: ${(props) => props.theme.size.static450};
    }

    @media (min-width: ${Breakpoints.b1600}px) {
        padding-left: ${(props) => props.theme.size.static550};
    }

    #${COUPON_LAYER_ID} > div > div > div:has(> &) {
        position: sticky;
        bottom: 0;
        background-color: ${(props) => props.theme.colors.background.primary};
        width: 100%;
        z-index: 1;
    }
`

// tslint:disable-next-line:cyclomatic-complexity
export function CouponRequestLayer(): React.JSX.Element {
    const {t} = useTranslation('fa-coupon')
    const dispatch = useDispatch() as AppDispatch

    const backendUrl = useSelector(
        (state: RootState) => state.general.backendUrl
    )
    const configurationPromo = useSelector(
        (state: RootState) => state.configurationPromo.configuration
    )
    const resentEmail = useSelector(
        (state: RootState) => state.userData.resentEmail
    )
    const thanksPage = useSelector(
        (state: RootState) => state.userData.thanksPage
    )
    const randomCode = useSelector(
        (state: RootState) => state.userData.randomCode
    )
    const personalData = useSelector(
        (state: RootState) => state.userData.personalData
    )
    const isDealerWeb = useSelector(
        (state: RootState) => state.general.isDealerWeb
    )

    const resendEmail = () => {
        axios
            .get(
                `${backendUrl}/${Endpoints.COUPONS}/${randomCode}/${Endpoints.EMAIL}`
            )
            .then(() => {
                dispatch(setResentEmail(true))

                trackingEvent(TrackingAction.VW_BASIC_LINK_CLICK, {
                    eventInfo: {
                        ...eventInfoCommon(),
                        contentId: t('layers.couponRequest.thanks.clickHere'),
                    },
                    link: {
                        name: t('layers.couponRequest.thanks.clickHere'),
                        newTab: false,
                        url: window.location.href,
                    },
                })
            })
            .catch((errors) => {
                dispatch(setLoading(false))

                openAlertSystemLayer()
                throw new Error(errors)
            })
    }

    const dynamicThankYouText = configurationPromo.thank_you_page.text[
        getVersionKey()
    ]
        .replace(':name', personalData.name)
        .replace(':surname', personalData.surname)
        .replace(':promotion', configurationPromo.name)
    const footerCtaText =
        configurationPromo.thank_you_page.footer.cta?.text[getVersionKey()]
    const footerCtaHref =
        configurationPromo.thank_you_page.footer.cta?.href[getSourceKey()]
    const labelSubmit = configurationPromo.form.cta_submit[getVersionKey()]
    const couponFormTitle = configurationPromo.form.title[getVersionKey()]
    const couponFormDescription =
        configurationPromo.form.description[getVersionKey()]?.trim()
    const couponThanksTitle =
        configurationPromo.thank_you_page.title[getVersionKey()]
    const layerTitle = thanksPage ? couponThanksTitle : couponFormTitle

    useEffect(() => {
        trackingEvent(TrackingAction.VW_BASIC_FORM_LAYER_LOAD, {
            eventInfo: {
                ...eventInfoCommon(),
                contentId:
                    store.getState().configurationPromo.configuration.form
                        .title[getVersionKey()],
            },
            form: {
                FormName: `${
                    store.getState().configurationPromo.configuration
                        .tracking_application_id
                } - ${
                    store.getState().configurationPromo.configuration.form
                        .title[getVersionKey()]
                }`,
                FormStart: true,
                FormType: trackingData.FORM_TYPE,
                CycleID: store.getState().userData.formCycleID,
            },
            process: {
                Name: store.getState().configurationPromo.configuration.form
                    .title[getVersionKey()],
                StepName:
                    store.getState().configurationPromo.configuration.form
                        .title[getVersionKey()],
            },
        })
    }, [])

    useEffect(() => {
        if (thanksPage) {
            document.getElementById(COUPON_LAYER_TITLE_ID)?.focus()
        }
    }, [thanksPage])

    return (
        <div id={COUPON_LAYER_ID}>
            <Container wrap="always">
                <CouponLayerHeaderContainer>
                    {layerTitle ? (
                        <LayerHeaderTitle
                            id={COUPON_LAYER_TITLE_ID}
                            tag={TextTag.h2}
                            bold
                            tabIndex={-1}
                            appearance={TokenTextAppearance.headline300}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: layerTitle,
                                }}
                            />
                        </LayerHeaderTitle>
                    ) : undefined}
                    <LayerHeaderCloseCTA
                        tag="button"
                        emphasis="tertiary"
                        aria-label={t('cta.closeLayerLabel')}
                        iconPosition="top"
                        onClick={() => {
                            closeCouponLayer()
                            trackingEvent(
                                TrackingAction.VW_BASIC_FORM_LAYER_CLOSE_CLICK,
                                {
                                    eventInfo: {
                                        ...eventInfoCommon(),
                                        contentId:
                                            store.getState().configurationPromo
                                                .configuration.form.title[
                                                getVersionKey()
                                            ],
                                    },
                                    form: {
                                        FormName: `${
                                            store.getState().configurationPromo
                                                .configuration
                                                .tracking_application_id
                                        } -
                                        ${
                                            store.getState().configurationPromo
                                                .configuration.form.title[
                                                getVersionKey()
                                            ]
                                        }`,
                                        FormStart: false,
                                        FormType: trackingData.FORM_TYPE,
                                    },
                                }
                            )
                        }}
                        icon={<Close aria-hidden />}
                    />
                </CouponLayerHeaderContainer>

                <Container
                    padding={{
                        left: {
                            [Breakpoints.default]: ContainerPadding.grid001,
                            [Breakpoints.b560]: ContainerPadding.static450,
                            [Breakpoints.b1600]: ContainerPadding.static550,
                        },
                        right: {
                            [Breakpoints.default]: ContainerPadding.grid001,
                            [Breakpoints.b560]: ContainerPadding.static450,
                            [Breakpoints.b1600]: ContainerPadding.static550,
                        },
                        top: ContainerPadding.static350,
                        bottom: ContainerPadding.static350,
                    }}
                >
                    {!thanksPage ? (
                        <>
                            <Layout rowGap={LayoutRowGap.static500}>
                                {couponFormDescription ? (
                                    <Text
                                        tag={TextTag.p}
                                        appearance={TokenTextAppearance.copy200}
                                    >
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: couponFormDescription,
                                            }}
                                        />
                                    </Text>
                                ) : undefined}
                                <Text
                                    tag={TextTag.p}
                                    appearance={TokenTextAppearance.copy100}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: configurationPromo.form
                                                .field_notes
                                                ? configurationPromo.form
                                                      .field_notes
                                                : t(
                                                      'layers.couponRequest.required'
                                                  ),
                                        }}
                                    />
                                </Text>
                            </Layout>
                            <CouponRequestForm />
                        </>
                    ) : (
                        <>
                            {configurationPromo && personalData ? (
                                <Text
                                    tag={TextTag.p}
                                    appearance={TokenTextAppearance.copy200}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: dynamicThankYouText
                                                ? dynamicThankYouText
                                                : undefined,
                                        }}
                                    />
                                </Text>
                            ) : undefined}

                            {configurationPromo.thank_you_email.enabled &&
                            configurationPromo.thank_you_page.resending
                                .enabled ? (
                                <ResentEmailTextContainer>
                                    {resentEmail ? (
                                        <ResentEmailText
                                            tag={TextTag.p}
                                            bold
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            <Checkmark aria-hidden />
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: configurationPromo
                                                        .thank_you_page
                                                        .resending
                                                        .confirmation_text[
                                                        getVersionKey()
                                                    ]
                                                        ? configurationPromo
                                                              .thank_you_page
                                                              .resending
                                                              .confirmation_text[
                                                              getVersionKey()
                                                          ]
                                                        : undefined,
                                                }}
                                            />
                                        </ResentEmailText>
                                    ) : (
                                        <Text
                                            tag={TextTag.p}
                                            appearance={
                                                TokenTextAppearance.copy200
                                            }
                                        >
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: configurationPromo
                                                        .thank_you_page
                                                        .resending.text[
                                                        getVersionKey()
                                                    ]
                                                        ? configurationPromo
                                                              .thank_you_page
                                                              .resending.text[
                                                              getVersionKey()
                                                          ]
                                                        : undefined,
                                                }}
                                            />
                                            <CTA
                                                tag="button"
                                                emphasis="tertiary"
                                                size="small"
                                                onClick={resendEmail}
                                                aria-label={t(
                                                    'layers.couponRequest.thanks.clickHereLabel'
                                                )}
                                                title={t(
                                                    'layers.couponRequest.thanks.clickHereLabel'
                                                )}
                                            >
                                                {t(
                                                    'layers.couponRequest.thanks.clickHere'
                                                )}
                                            </CTA>
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: t(
                                                        'layers.couponRequest.thanks.text2'
                                                    ),
                                                }}
                                            />
                                        </Text>
                                    )}
                                </ResentEmailTextContainer>
                            ) : undefined}

                            {configurationPromo.thank_you_page.footer
                                .enabled ? (
                                <FooterTextContainer>
                                    <Text
                                        tag={TextTag.p}
                                        appearance={TokenTextAppearance.copy200}
                                    >
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: configurationPromo
                                                    .thank_you_page.footer.text[
                                                    getVersionKey()
                                                ]
                                                    ? configurationPromo
                                                          .thank_you_page.footer
                                                          .text[getVersionKey()]
                                                    : undefined,
                                            }}
                                        />
                                    </Text>

                                    <ContainerFooterCTA>
                                        <Layout rowGap={LayoutRowGap.static350}>
                                            {configurationPromo.thank_you_page
                                                .footer.cta ? (
                                                <Container horizontalAlign="center">
                                                    <CTA
                                                        tag="a"
                                                        target={
                                                            isDealerWeb
                                                                ? '_self'
                                                                : '_blank'
                                                        }
                                                        emphasis="primary"
                                                        href={
                                                            footerCtaHref
                                                                ? footerCtaHref
                                                                : undefined
                                                        }
                                                        onClick={() => {
                                                            trackingEvent(
                                                                TrackingAction.VW_BASIC_BUTTON_CLICK,
                                                                {
                                                                    eventInfo: {
                                                                        ...eventInfoCommon(),
                                                                        contentId:
                                                                            footerCtaText,
                                                                    },
                                                                    link: {
                                                                        name: footerCtaText,
                                                                        url: footerCtaHref,
                                                                        newTab: true,
                                                                    },
                                                                }
                                                            )
                                                        }}
                                                    >
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: footerCtaText
                                                                    ? footerCtaText
                                                                    : undefined,
                                                            }}
                                                        />
                                                    </CTA>
                                                </Container>
                                            ) : undefined}

                                            <Container horizontalAlign="center">
                                                <CTA
                                                    tag="button"
                                                    emphasis="secondary"
                                                    onClick={() => {
                                                        closeCouponLayer()
                                                        trackingEvent(
                                                            TrackingAction.VW_BASIC_BUTTON_CLICK,
                                                            {
                                                                eventInfo: {
                                                                    ...eventInfoCommon(),
                                                                    contentId:
                                                                        t(
                                                                            'layers.couponRequest.thanks.cta'
                                                                        ),
                                                                },
                                                                link: {
                                                                    name: t(
                                                                        'layers.couponRequest.thanks.cta'
                                                                    ),
                                                                    url: window
                                                                        .location
                                                                        .href,
                                                                    newTab: false,
                                                                },
                                                            }
                                                        )
                                                    }}
                                                    title={t(
                                                        'layers.couponRequest.thanks.ctaLabel'
                                                    )}
                                                    aria-label={t(
                                                        'layers.couponRequest.thanks.ctaLabel'
                                                    )}
                                                >
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html: t(
                                                                'layers.couponRequest.thanks.cta'
                                                            ),
                                                        }}
                                                    />
                                                </CTA>
                                            </Container>
                                        </Layout>
                                    </ContainerFooterCTA>
                                </FooterTextContainer>
                            ) : (
                                <Container horizontalAlign="center">
                                    <CloseLayerCTA
                                        tag="button"
                                        emphasis="secondary"
                                        onClick={() => closeCouponLayer()}
                                        title={t(
                                            'layers.couponRequest.thanks.ctaLabel'
                                        )}
                                        aria-label={t(
                                            'layers.couponRequest.thanks.ctaLabel'
                                        )}
                                    >
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t(
                                                    'layers.couponRequest.thanks.ctaLabel'
                                                ),
                                            }}
                                        />
                                    </CloseLayerCTA>
                                </Container>
                            )}
                        </>
                    )}
                </Container>

                {!thanksPage ? (
                    <SubmitContainer>
                        <CTA
                            tag="button"
                            emphasis="primary"
                            type="submit"
                            {...({form: COUPON_REQUEST_FORM_ID} as {
                                form: string
                            })}
                            title={labelSubmit}
                            aria-label={labelSubmit ? labelSubmit : undefined}
                        >
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: labelSubmit ? labelSubmit : '',
                                }}
                            />
                        </CTA>
                    </SubmitContainer>
                ) : undefined}
            </Container>
        </div>
    )
}
